import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageMeta from '../seo/PageMeta';
import JsonLd from '../seo/JsonLd';
import { getSiteOrigin } from '../../utils/siteOrigin';
import { formatBlogDate } from '../../utils/formatBlogDate';
import { computeArticleSeo } from '../../utils/articleSeo';
import {
  fetchBlogManifest,
  fetchPostMarkdown,
  postDatePublished,
  type ParsedPost,
} from '../../utils/blogMarkdown';
import { isSafariBrowser, scrollReveal } from '../../utils/safariMotion';
import './BlogIndex.css';

const BLOG_INDEX_TITLE = 'Blog | Substyle — Premiere Pro subtitles & tips';
const BLOG_INDEX_DESCRIPTION =
  'Guides, tutorials, and updates on animated subtitles in Adobe Premiere Pro. Learn how Substyle helps editors ship accessible, localized video and grow visibility in search.';

type ListItem = {
  slug: string;
  title: string;
  description: string;
  datePublished?: string;
};

function BlogIndex() {
  const safari = isSafariBrowser();
  const [items, setItems] = useState<ListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const slugs = await fetchBlogManifest();
        const results = await Promise.all(
          slugs.map(async (slug) => {
            try {
              const parsed: ParsedPost = await fetchPostMarkdown(slug);
              const seo = computeArticleSeo(parsed);
              const datePublished = postDatePublished(parsed);
              const item: ListItem = {
                slug,
                title: seo.articleTitle,
                description: seo.metaDescription,
                ...(datePublished ? { datePublished } : {}),
              };
              return item;
            } catch {
              return null;
            }
          })
        );
        if (cancelled) return;
        setItems(results.filter((r): r is ListItem => r != null));
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : 'Could not load the blog list.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const origin = getSiteOrigin();
  const blogJsonLd = useMemo(() => {
    const base = origin.replace(/\/$/, '');
    return {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Substyle Blog',
      description: BLOG_INDEX_DESCRIPTION,
      url: `${base}/blog`,
      publisher: {
        '@type': 'Organization',
        name: 'CODEC422',
        url: base || undefined,
      },
      blogPost: items.map((item) => ({
        '@type': 'BlogPosting',
        headline: item.title,
        description: item.description,
        url: `${base}/blog/${encodeURIComponent(item.slug)}`,
        datePublished: item.datePublished,
      })),
    };
  }, [items, origin]);

  return (
    <main className="blog-page">
      <PageMeta
        title={BLOG_INDEX_TITLE}
        description={BLOG_INDEX_DESCRIPTION}
        canonicalPath="/blog"
        keywords="Premiere Pro subtitles, animated captions inside premiere pro, subtitle extension"
      />
      <JsonLd id="substyle-blog-jsonld" data={blogJsonLd} />

      <section className="blog-hero" aria-labelledby="blog-heading">
        <div className="container blog-hero-inner">
          <motion.div
            transition={{ duration: 0.6 }}
            {...scrollReveal(
              safari,
              { opacity: 0, y: 16 },
              { opacity: 1, y: 0 },
              'section'
            )}>
            <span className="blog-eyebrow">Substyle</span>
            <h1 id="blog-heading">Blog</h1>
            <p className="blog-hero-lead">
              Substyle's blog for Premiere Pro editors.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="blog-list-wrap" aria-label="Articles">
        <div className="container">
          <div className="blog-list-head">
            <h2>Latest articles</h2>
            <p>
              Deep dives, workflows, and product updates—written for editors who value their time.
            </p>
          </div>

          {loading && (
            <div className="blog-loading" role="status">
              <div className="blog-loading-spinner" aria-hidden />
              <p>Loading articles…</p>
            </div>
          )}
          {error && (
            <p className="blog-state blog-state-error" role="alert">
              {error}
            </p>
          )}
          {!loading && !error && items.length === 0 && (
            <p className="blog-state">No articles yet. Check back soon.</p>
          )}
          {!loading && !error && items.length > 0 && (
            <div className="blog-grid">
              {items.map((item, index) => (
                <motion.div
                  key={item.slug}
                  transition={{ duration: 0.45, delay: index * 0.06 }}
                  {...scrollReveal(
                    safari,
                    { opacity: 0, y: 14 },
                    { opacity: 1, y: 0 },
                    'nested'
                  )}>
                  <Link to={`/blog/${item.slug}`} className="blog-card">
                    <div className="blog-card-body">
                      {item.datePublished && (
                        <time dateTime={item.datePublished}>
                          {formatBlogDate(item.datePublished)}
                        </time>
                      )}
                      <h2>{item.title}</h2>
                      <p>{item.description}</p>
                    </div>
                    <div className="blog-card-footer">
                      <span className="blog-card-cta">Read article</span>
                      <span className="blog-card-arrow" aria-hidden>
                        →
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

export default BlogIndex;
